import React, { useEffect, useState } from "react";
import MarketingHeader from "../components/argus/MarketingHeader";
import Footer from "../components/argus/Footer";
import { api } from "../lib/api";
import { ShieldCheck, Lock, GitBranch, AlertTriangle, ExternalLink, FileText } from "lucide-react";

const SectionTitle = ({ icon: Icon, children, testid }) => (
  <div className="flex items-center gap-3 mb-4 mt-12" data-testid={testid}>
    <Icon size={22} className="text-gold shrink-0" />
    <h2 className="font-display text-3xl tracking-tight">{children}</h2>
  </div>
);

const KV = ({ k, v }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-2 border-b border-line/60 last:border-b-0">
    <dt className="text-ink/55 text-sm font-medium">{k}</dt>
    <dd className="text-ink sm:col-span-2 text-sm leading-relaxed">{v}</dd>
  </div>
);

export default function Trust() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/trust")
      .then((r) => setData(r.data))
      .catch(() => setErr("Couldn't load the trust artifact. Try again in a moment."));
  }, []);

  if (err) {
    return (
      <div className="bg-paper text-ink min-h-screen flex flex-col">
        <MarketingHeader />
        <section className="flex-1 mx-auto max-w-3xl w-full px-4 py-16 text-ink/60">{err}</section>
        <Footer />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-paper text-ink min-h-screen flex flex-col">
        <MarketingHeader />
        <section className="flex-1 mx-auto max-w-3xl w-full px-4 py-16 text-ink/40">Loading…</section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-paper text-ink min-h-screen flex flex-col">
      <MarketingHeader />
      <section className="flex-1 mx-auto max-w-4xl w-full px-4 sm:px-6 lg:px-8 py-16" data-testid="trust-page">
        <div className="flex items-center gap-3 mb-3">
          <ShieldCheck size={28} className="text-gold" />
          <h1 className="font-display text-5xl tracking-tight" data-testid="trust-title">Trust.</h1>
        </div>
        <p className="text-ink/65 max-w-2xl">
          What we ship, how it's verified, what's encrypted, and how we respond when an upstream OSS dependency
          has a CVE. Every number on this page comes from live backend state — nothing is fabricated.
        </p>
        <p className="text-xs text-ink/40 mt-2 font-mono" data-testid="trust-asof">As of {data.as_of?.slice(0, 19)} UTC</p>

        {/* ENGINES */}
        <SectionTitle icon={GitBranch} testid="trust-section-engines">Embedded scanner engines</SectionTitle>
        <p className="text-sm text-ink/65 mb-4">
          Open-source scanners we ship with ArgusSecure. Each external binary is pinned to a specific version and
          verified against the project's official <code className="text-gold">*_checksums.txt</code> at install
          time. Hash mismatch = archive quarantined, binary not extracted.
        </p>
        <div className="rounded-xl border border-line bg-panel overflow-hidden" data-testid="trust-engines-table">
          <table className="w-full text-sm">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Engine</th>
                <th className="text-left px-4 py-2.5">Category</th>
                <th className="text-left px-4 py-2.5">Version</th>
                <th className="text-left px-4 py-2.5">SHA-256 (pin)</th>
              </tr>
            </thead>
            <tbody>
              {data.engines.map((e) => (
                <tr key={e.id} className="border-t border-line" data-testid={`trust-engine-${e.id}`}>
                  <td className="px-4 py-2">
                    <a href={e.upstream_url || "#"} target="_blank" rel="noopener noreferrer" className="hover:text-gold inline-flex items-center gap-1">
                      {e.name}{e.upstream_url && <ExternalLink size={10} />}
                    </a>
                  </td>
                  <td className="px-4 py-2 uppercase text-xs text-ink/55">{e.category}</td>
                  <td className="px-4 py-2 font-mono text-xs">{e.pinned_version || "built-in"}</td>
                  <td className="px-4 py-2 font-mono text-xs text-ink/55">{e.expected_sha256 || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ENCRYPTION */}
        <SectionTitle icon={Lock} testid="trust-section-encryption">Encryption at rest</SectionTitle>
        <dl className="rounded-xl border border-line bg-panel p-5" data-testid="trust-encryption">
          <KV k="Algorithm" v={data.encryption_at_rest.algorithm} />
          <KV k="Scope" v={data.encryption_at_rest.scope} />
          <KV k="Key management" v={data.encryption_at_rest.key_management} />
          <KV k="What is NOT encrypted" v={data.encryption_at_rest.what_is_NOT_encrypted} />
        </dl>

        {/* SUPPLY CHAIN CVEs */}
        <SectionTitle icon={AlertTriangle} testid="trust-section-supply-chain">Supply-chain CVEs we've responded to</SectionTitle>
        <p className="text-sm text-ink/65 mb-4">
          Open-source scanner engines themselves get CVEs. Here are the upstream incidents we've taken action on.
        </p>
        <div className="space-y-3" data-testid="trust-cves">
          {data.supply_chain_posture.responded_cves.map((cve) => (
            <article key={cve.id} className="rounded-xl border border-line bg-panel p-5" data-testid={`trust-cve-${cve.id.toLowerCase()}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-mono font-bold">{cve.id}</h3>
                <span className="text-xs text-sev-medium uppercase tracking-wider">{cve.severity}</span>
              </div>
              <p className="text-sm text-ink/70 mt-2"><span className="text-ink/45">Scope:</span> {cve.scope}</p>
              <p className="text-sm text-ink/85 mt-1"><span className="text-ink/45">Our action:</span> {cve.our_action}</p>
            </article>
          ))}
        </div>

        {/* AUTH */}
        <SectionTitle icon={ShieldCheck} testid="trust-section-auth">Authentication & authorization</SectionTitle>
        <dl className="rounded-xl border border-line bg-panel p-5" data-testid="trust-auth">
          <KV k="Passwords" v={data.authentication_authorization.passwords} />
          <KV k="Session" v={data.authentication_authorization.session} />
          <KV k="Email verification" v={data.authentication_authorization.email_verification} />
          <KV k="Password reset" v={data.authentication_authorization.password_reset} />
          <KV k="Tenant isolation" v={data.authentication_authorization.rls} />
          <KV k="Audit log" v={data.authentication_authorization.audit_log} />
          <KV k="Rate limiting" v={data.authentication_authorization.rate_limiting} />
        </dl>

        {/* SCAN POSTURE */}
        <SectionTitle icon={FileText} testid="trust-section-scan-posture">Scanning posture</SectionTitle>
        <dl className="rounded-xl border border-line bg-panel p-5" data-testid="trust-scan-posture">
          <KV k="Honest copywriting" v={data.scanning_posture.honest_copywriting} />
          <KV k="False-positive rationale" v={data.scanning_posture.false_positive_rationale} />
          <KV k="Evidence provenance" v={data.scanning_posture.evidence_provenance} />
        </dl>

        {/* DATA HANDLING */}
        <SectionTitle icon={Lock} testid="trust-section-data">Data handling</SectionTitle>
        <dl className="rounded-xl border border-line bg-panel p-5" data-testid="trust-data">
          <KV k="Storage" v={data.data_handling.storage} />
          <KV k="Exports" v={data.data_handling.exports} />
          <KV k="Retention" v={data.data_handling.retention} />
          <KV k="Evidence in PDFs" v={data.data_handling.evidence_in_pdf} />
        </dl>

        {/* LICENSES */}
        <SectionTitle icon={FileText} testid="trust-section-licenses">Open-source licenses</SectionTitle>
        <p className="text-sm text-ink/65 mb-4">
          ArgusSecure ships the following OSS engines. We attribute each upstream project here and on the
          per-engine page in <a href="/admin" className="text-gold hover:underline">Admin → Engines</a>.
        </p>
        <div className="rounded-xl border border-line bg-panel overflow-hidden" data-testid="trust-licenses-table">
          <table className="w-full text-sm">
            <thead className="bg-raised text-ink/60 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5">Component</th>
                <th className="text-left px-4 py-2.5">License</th>
              </tr>
            </thead>
            <tbody>
              {data.open_source_licenses.map((l, i) => (
                <tr key={i} className="border-t border-line" data-testid={`trust-license-${i}`}>
                  <td className="px-4 py-2 font-medium">{l.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">
                    {l.license}
                    {l.notice && <div className="text-sev-medium text-[11px] mt-1 font-sans not-italic">{l.notice}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* RESPONSIBLE DISCLOSURE */}
        <SectionTitle icon={AlertTriangle} testid="trust-section-disclosure">Responsible disclosure</SectionTitle>
        <div className="rounded-xl border border-line bg-panel p-5" data-testid="trust-disclosure">
          <p className="text-sm text-ink/80">Found something? Mail us at <a href={`mailto:${data.responsible_disclosure.contact}`} className="text-gold hover:underline">{data.responsible_disclosure.contact}</a>.</p>
          <p className="text-xs text-ink/55 mt-3"><span className="text-ink/45">Scope:</span> {data.responsible_disclosure.scope}</p>
          <p className="text-xs text-ink/55 mt-1">
            <a href={data.responsible_disclosure.policy_url} className="text-gold hover:underline inline-flex items-center gap-1">
              Full policy <ExternalLink size={10} />
            </a>
          </p>
        </div>

        <div className="mt-16 text-xs text-ink/40 font-mono" data-testid="trust-footer-note">
          Live data sourced from <code>GET /api/v1/trust</code>. Pinned versions and SHA-256 anchors are read
          from <code>scanner_bootstrap.pin_report()</code>; CVE response history is hand-maintained.
        </div>
      </section>
      <Footer />
    </div>
  );
}
